import { morph } from '@theme/morph';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * PcdShopTrigger — fetches a product page and opens the theme's quick-add
 * dialog. Mirrors QuickAddComponent.updateQuickAddModal() so the rendered
 * modal contents match what users get from a regular quick-add button.
 *
 * Usage:
 *   <pcd-shop-trigger data-product-url="/products/my-product" data-product-title="...">
 *     <button>Shop this look</button>
 *   </pcd-shop-trigger>
 */
class PcdShopTrigger extends HTMLElement {
  /** @type {Map<string, Element>} */
  #cache = new Map();

  connectedCallback() {
    this.addEventListener('click', (e) => {
      if (!e.target.closest('button')) return;
      e.preventDefault();
      this.#trigger();
    });
  }

  async #trigger() {
    const url = this.dataset.productUrl;
    if (!url) return;

    const dialog = document.getElementById('quick-add-dialog');
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!dialog || !modalContent) return;

    let productGrid = this.#cache.get(url);

    if (!productGrid) {
      try {
        const response = await fetch(url);
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = doc.querySelector('[data-product-grid-content]');
        if (grid) {
          productGrid = /** @type {Element} */ (grid.cloneNode(true));
          this.#cache.set(url, productGrid);
        }
      } catch {
        return;
      }
    }

    if (!productGrid) return;

    // Fresh clone for this open
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));

    // Mirror QuickAddComponent.updateQuickAddModal: on mobile, restructure the
    // product grid so the modal shows title/price + variant picker + form.
    if (isMobileBreakpoint()) {
      const productDetails = freshContent.querySelector('.product-details');
      const productFormComponent = freshContent.querySelector('product-form-component');
      const variantPicker = freshContent.querySelector('variant-picker');
      const productPrice = freshContent.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = url;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);
      if (productPrice) productHeader.appendChild(productPrice);
      freshContent.appendChild(productHeader);

      if (variantPicker) freshContent.appendChild(variantPicker);

      // 'Show product' link sits ABOVE the Add to cart button on mobile
      const showProductLinkMobile = document.createElement('a');
      showProductLinkMobile.href = url;
      showProductLinkMobile.className = 'pcd-modal__show-product';
      showProductLinkMobile.textContent = 'Show product';
      freshContent.appendChild(showProductLinkMobile);

      if (productFormComponent) freshContent.appendChild(productFormComponent);

      productDetails?.remove();
    } else {
      // Desktop: append 'Show product' link at the bottom (after the full product details)
      if (!freshContent.querySelector('.pcd-modal__show-product')) {
        const showProductLink = document.createElement('a');
        showProductLink.href = url;
        showProductLink.className = 'pcd-modal__show-product';
        showProductLink.textContent = 'Show product';
        freshContent.appendChild(showProductLink);
      }
    }

    morph(modalContent, freshContent);

    if (typeof dialog.showDialog === 'function') {
      dialog.showDialog();
    }
  }
}

if (!customElements.get('pcd-shop-trigger')) {
  customElements.define('pcd-shop-trigger', PcdShopTrigger);
}
